import { EventType } from "@/analytics/analytics-events";
import { GenerateSnapshotEventProperties } from "@/analytics/snapshot-events-properties";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import {
  isLightweightGenericSheet,
  isLightweightImg360,
} from "@/store/lightweight-elements-discriminators";
import {
  selectLightweightChildren,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import {
  hideLightweightElements,
  LightweightElement,
  LightweightElementsRecord,
  LightweightElementsStore,
} from "@/store/lightweight-elements-slice";
import { curryAppSelector } from "@/store/reselect";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { deactivateTool } from "@/store/ui/ui-slice";
import { selectCurrentUser } from "@/store/user-selectors";
import { selectVideoWalksToLoad } from "@/tools/create-report/create-report-selectors";
import {
  Alert,
  blue,
  CircularProgress,
  Dropdown,
  DropdownOption,
  FaroButton,
  FaroDialog,
  FaroDialogProps,
  FaroText,
  FaroTooltip,
  InfoIcon,
  grey,
  TextField,
  TranslateVar,
  useToast,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  convertToDateString,
  GUID,
  humanReadableBytes,
  sanitizeFileName,
} from "@faro-lotv/foundation";
import { IElementType, IElementTypeHint } from "@faro-lotv/ielement-types";
import {
  hideIElements,
  selectProjectId,
  selectProjectName,
  selectRootId,
} from "@faro-lotv/project-source";
import {
  ProjectAccessLevel,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import Stack from "@mui/material/Stack";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { updateProject } from "../common/project-provider/update-project";
import { useCaptureTreeContext } from "./tree/capture-tree/capture-tree-context";
import { useCaptureTreeSelectionStore } from "./tree/capture-tree/capture-tree-selection-store";
import { UsersInvite } from "./users-invite/users-invite";

type SnapshotConfigurationDialogProps = Pick<
  FaroDialogProps,
  "open" | "onCancel" | "onClose"
>;

/**
 * @returns The dialog containing the options to configure a PDF report
 */
export function SnapshotConfigurationDialog({
  open,
  ...props
}: SnapshotConfigurationDialogProps): JSX.Element | null {
  // Unmount everything when the dialog is closed
  if (!open) {
    return null;
  }

  return <DialogWithLoader open={open} {...props} />;
}

/**
 * @returns The dialog containing the options to configure a PDF report
 */
export function DialogWithLoader({
  ...props
}: SnapshotConfigurationDialogProps): JSX.Element {
  const { selectedLeaves } = useCaptureTreeContext();

  const currentUser = useAppSelector(selectCurrentUser);
  const projectId = useAppSelector(selectProjectId);
  const projectName = useAppSelector(selectProjectName);

  const defaultSnapshotName = useMemo(
    () =>
      sanitizeFileName(
        `${projectName} ${convertToDateString(DateTime.now().toISO())}`,
      ),
    [projectName],
  );
  const [snapshotName, setSnapshotName] = useState<string>("");

  const { handleErrorWithToast } = useErrorHandlers();

  const dispatch = useAppDispatch();

  const { projectApiClient } = useApiClientContext();

  const [isProcessing, setIsProcessing] = useState(false);

  const { openToast } = useToast();

  const selectedIds = useCaptureTreeSelectionStore((s) => s.selectedIds);

  const elementsIds = useAppSelector(selectSnapshotTree(selectedIds), isEqual);

  // Load all the video walks dataset in all the areas.
  const videoWalksToLoad = useAppSelector(
    selectVideoWalksToLoad(selectedLeaves),
  );
  const [initialList] = useState(videoWalksToLoad);
  useEffect(() => {
    if (!initialList.length) return;

    const controller = new AbortController();

    async function loadDataset(): Promise<void> {
      if (!initialList.length) return;

      await dispatch(
        updateProject({
          projectApi: projectApiClient,
          iElementQuery: {
            signal: controller.signal,
            ancestorIds: initialList,
          },
        }),
      );
    }
    loadDataset();

    return () => controller.abort();
  }, [dispatch, projectApiClient, initialList]);

  // Compute memory footprint
  const [memory, setMemory] = useState<number | undefined>();
  useEffect(() => {
    if (videoWalksToLoad.length) {
      return;
    }

    const controller = new AbortController();

    async function loadMemoryFootPrint(): Promise<void> {
      setMemory(undefined);

      const iElements = await projectApiClient.getAllIElements({
        signal: controller.signal,
        ids: elementsIds,
      });

      let size = 0;
      for (const iElement of iElements) {
        if ("fileSize" in iElement) {
          size += iElement.fileSize ?? 0;
        }
      }
      setMemory(size);
    }

    loadMemoryFootPrint();
  }, [elementsIds, projectApiClient, videoWalksToLoad]);

  const [accessLevel, setAccessLevel] = useState<
    ProjectAccessLevel.unlisted | ProjectAccessLevel.private
  >(ProjectAccessLevel.unlisted);
  const [emails, setEmails] = useState<string[]>([]);

  const createReport = useCallback(async () => {
    if (!currentUser?.id || !projectId || !selectedLeaves.length) {
      return false;
    }

    setIsProcessing(true);

    Analytics.track<GenerateSnapshotEventProperties>(
      EventType.generateSnapshot,
      {
        access:
          accessLevel === ProjectAccessLevel.private ? "private" : "unlisted",
        nameChanged: snapshotName !== defaultSnapshotName,
        numberOfLocations: selectedLeaves.length,
        numberOfInvites: emails.length,
      },
    );

    try {
      await projectApiClient.createProjectSnapshot({
        snapshotName: snapshotName
          ? sanitizeFileName(snapshotName)
          : defaultSnapshotName,
        accessLevel,
        ...(accessLevel === ProjectAccessLevel.private
          ? {
              users: {
                byId: [],
                byEmail: emails,
              },
            }
          : undefined),
        iElementIds: elementsIds,
      });

      openToast({
        variant: "info",
        title: "Snapshot is being created",
        message:
          "The link will be available soon. Check the progress in the Cloud Activity.",
      });

      dispatch(deactivateTool());
      dispatch(hideIElements([]));
      dispatch(hideLightweightElements([]));
    } catch (e) {
      handleErrorWithToast({
        title: "Report generation failed",
        error: e,
      });
    }

    setIsProcessing(false);
  }, [
    accessLevel,
    currentUser?.id,
    defaultSnapshotName,
    dispatch,
    elementsIds,
    emails,
    handleErrorWithToast,
    openToast,
    projectApiClient,
    projectId,
    selectedLeaves.length,
    snapshotName,
  ]);

  const shareOptions = useMemo<DropdownOption[]>(
    () => [
      {
        key: ProjectAccessLevel.private,
        label: "Only invited people",
        value: ProjectAccessLevel.private,
      },
      {
        key: ProjectAccessLevel.unlisted,
        label: "Anyone with the link",
        value: ProjectAccessLevel.unlisted,
      },
    ],
    [],
  );

  const isLoadingVideoData = !!videoWalksToLoad.length;

  return (
    <FaroDialog
      title="Create snapshot"
      showSpinner={isProcessing}
      disabled={isProcessing}
      {...props}
    >
      <Stack aria-label="snapshot-dialog-content" sx={{ gap: 3 }}>
        <Alert variant="neutral" title="Overview" sx={{ color: grey[600] }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              pt: 1,
            }}
          >
            <FaroText variant="bodyM">
              Selected locations:{" "}
              <FaroText variant="heading14">
                <TranslateVar name="snapshotSelectedElements">
                  {selectedLeaves.length}
                </TranslateVar>
              </FaroText>
            </FaroText>
            <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
              <FaroText variant="bodyM">
                Size:{" "}
                {memory ? (
                  <FaroText variant="heading14">
                    <TranslateVar name="snapshotByteSize">
                      {humanReadableBytes(memory, 1)}
                    </TranslateVar>
                  </FaroText>
                ) : (
                  ""
                )}
              </FaroText>
              {memory === undefined && (
                <CircularProgress size="0.75rem" sx={{ color: blue[500] }} />
              )}
              <FaroTooltip title="A snapshot is a project copy and will therefore occupy storage capacity in your workspace.">
                <InfoIcon sx={{ width: "16px", height: "16px", mt: 0.5 }} />
              </FaroTooltip>
            </Stack>
          </Stack>
        </Alert>
        <TextField
          label="Snapshot name"
          placeholder={defaultSnapshotName}
          text={snapshotName}
          onTextChanged={setSnapshotName}
          fullWidth
        />
        <Dropdown
          label="Share with"
          options={shareOptions}
          value={accessLevel}
          onChange={(e) => {
            const { value } = e.target;
            if (isAccessLevel(value)) {
              setAccessLevel(value);
            }
          }}
          fullWidth
          shouldCapitalize={false}
        />
        {accessLevel === ProjectAccessLevel.private && (
          <UsersInvite onEmailsChanged={setEmails} />
        )}
        <Stack sx={{ gap: 1 }}>
          <FaroTooltip
            title={
              isLoadingVideoData
                ? "Loading the selected data to generate the snapshot. This will only take a moment."
                : null
            }
          >
            <FaroButton
              sx={{ width: "100%" }}
              onClick={createReport}
              isLoading={isProcessing}
              aria-label="confirm"
              disabled={
                isLoadingVideoData ||
                (accessLevel === ProjectAccessLevel.private && !emails.length)
              }
            >
              Create snapshot
            </FaroButton>
          </FaroTooltip>
          {isLoadingVideoData && (
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                height: "16px",
                width: "100%",
              }}
            >
              <CircularProgress
                size={16}
                sx={{
                  color: blue[500],
                  width: "16px",
                  height: "16px",
                  overflow: "hidden",
                }}
              />
              <FaroText variant="bodyS">Loading selected data...</FaroText>
            </Stack>
          )}
        </Stack>
      </Stack>
    </FaroDialog>
  );
}

// Add all ancestors of an iElement to the input tree
function flagParent(
  element: LightweightElement,
  lightweightElements: LightweightElementsRecord,
  tree: Set<GUID>,
  rootId: GUID,
): void {
  let parentNodeId: string | undefined | null = element.id;

  while (parentNodeId && parentNodeId !== rootId) {
    const parent: LightweightElement | undefined =
      lightweightElements[parentNodeId];
    if (!parent) return;

    tree.add(parentNodeId);

    parentNodeId = parent.parentId;
  }
}

// Given the list of elements selected by the user, collect all supported 360s, 2D images,
// sheets and all related ancestors that must be provided to the Project API for the snapshot
// creation
const selectSnapshotTree = curryAppSelector(
  createSelector(
    [
      selectRootId,
      selectLightweightElementsSlice,
      selectIElementsSlice,
      (state: LightweightElementsStore, selectedIds: Set<GUID>) => selectedIds,
    ],
    (rootId, lightweightElementsState, iElementsState, selectedIds) => {
      if (!rootId) return [];

      const { lightweightElements } = lightweightElementsState;

      const tree = new Set<GUID>();

      for (const id of selectedIds) {
        const element = lightweightElements[id];
        if (!element) continue;

        if (
          element.type === IElementType.section &&
          element.typeHint === IElementTypeHint.area
        ) {
          const sheets = selectLightweightChildren(
            element,
            isLightweightGenericSheet,
          )(lightweightElementsState);
          for (const sheet of sheets) {
            flagParent(sheet, lightweightElements, tree, element.id);
          }
        } else if (
          element.type === IElementType.section &&
          (element.typeHint === IElementTypeHint.timeTravel ||
            element.typeHint === IElementTypeHint.room)
        ) {
          const { parentId } = element;
          if (!parentId) continue;

          const parent = lightweightElements[parentId];
          if (
            !parent ||
            parent.type !== IElementType.timeSeries ||
            parent.typeHint !== IElementTypeHint.room
          ) {
            continue;
          }

          const img360s = selectLightweightChildren(
            element,
            isLightweightImg360,
          )(lightweightElementsState);
          for (const img360 of img360s) {
            flagParent(img360, lightweightElements, tree, element.id);
          }
        } else if (
          element.type === IElementType.section &&
          element.typeHint === IElementTypeHint.dataSession
        ) {
          // Flag all descendants of a data session
          const stack = [...(element.childrenIds ?? [])];
          while (stack.length) {
            const childId = stack.pop();
            if (!childId) break;

            // We check both the iElement and the lightweight iElement
            // because some elements may be in one structure but not in the other
            // TODO: let's fix it by either not having two different structures
            // or by ensuring they are always in sync
            const child = lightweightElements[childId];
            const childIElement = iElementsState.iElements[childId];

            if (!child && !childIElement) break;

            tree.add(childId);

            if (child?.childrenIds) {
              stack.push(...child.childrenIds);
            } else if (childIElement?.childrenIds) {
              stack.push(...childIElement.childrenIds);
            }

            // We need the iElement because targetId is not
            // a property of lightweight elements
            if (
              childIElement &&
              "targetId" in childIElement &&
              !!childIElement.targetId &&
              typeof childIElement.targetId === "string"
            ) {
              const target = lightweightElements[childIElement.targetId];
              if (target) {
                const img360s = selectLightweightChildren(
                  target,
                  isLightweightImg360,
                )(lightweightElementsState);
                for (const img360 of img360s) {
                  flagParent(img360, lightweightElements, tree, element.id);
                }
              }
            }
          }
        }

        flagParent(element, lightweightElements, tree, rootId);
      }

      tree.delete(rootId);

      return [...tree];
    },
  ),
);

function isAccessLevel(
  x: string,
): x is ProjectAccessLevel.unlisted | ProjectAccessLevel.private {
  return x === ProjectAccessLevel.unlisted || x === ProjectAccessLevel.private;
}
